import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useClient } from 'urql'
import { GetTimeClockQuery } from '../graphql/timeclock'
import { useNetwork } from '../hooks/useNetwork'
import { useToast } from '../hooks/useToast'
import { useGraphQLCache } from './GraphQLProvider/Cache'
import { offlineSyncStore } from './GraphQLProvider/offlineSync'
import { usePhotoUpload } from './PhotoUploadProvider'

type SyncStatus = {
  /** ISO timestamp of the last successful server round-trip, or null. */
  lastSyncAt: string | null
  /** Number of mutations queued offline and awaiting replay. */
  pendingCount: number
  /** Background mutations rejected by the server (optimistic state rolled back). */
  failureCount: number
  /** True while a manual sync is in flight. */
  isSyncing: boolean
  /** Manually flush the offline queue and refresh key data. */
  forceSync: () => Promise<void>
}

const SyncStatusContext = createContext<SyncStatus>({
  lastSyncAt: null,
  pendingCount: 0,
  failureCount: 0,
  isSyncing: false,
  forceSync: () => Promise.resolve(),
})

export const useSyncStatus = () => useContext(SyncStatusContext)

/**
 * Surfaces offline-sync state for the UI and bridges connectivity events.
 *
 * urql graphcache replays its offline queue when the browser fires a `window`
 * `online` event. Inside a Capacitor webview that event is unreliable, so we
 * re-dispatch it whenever `@capacitor/network` reports we're back online — this
 * is what actually drains the queue on a real device.
 *
 * Must render inside both the urql `Provider` (for `useClient`) and
 * `NetworkProvider` (for `useNetwork`).
 */
export const SyncStatusProvider = ({ children }: React.PropsWithChildren) => {
  const client = useClient()
  const { isOnline } = useNetwork()
  const { cache } = useGraphQLCache()
  const { syncPhotos, pendingPhotos } = usePhotoUpload()

  const lastSyncAt = useSyncExternalStore(
    offlineSyncStore.subscribe,
    offlineSyncStore.getLastSyncAt,
    offlineSyncStore.getLastSyncAt,
  )
  const failureCount = useSyncExternalStore(
    offlineSyncStore.subscribe,
    offlineSyncStore.getFailureCount,
    offlineSyncStore.getFailureCount,
  )
  const [pendingCount, setPendingCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  const refreshPending = useCallback(async () => {
    try {
      const meta = await cache.readMetadata?.()
      setPendingCount(Array.isArray(meta) ? meta.length : 0)
    } catch {
      /* ignore — keep the previous count */
    }
  }, [cache])

  // Poll the persisted offline queue length.
  useEffect(() => {
    refreshPending()
    const interval = setInterval(refreshPending, 8000)
    return () => clearInterval(interval)
  }, [refreshPending])

  // Re-check the queue right after a successful sync.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when lastSyncAt changes
  useEffect(() => {
    refreshPending()
  }, [lastSyncAt, refreshPending])

  // Bridge: when @capacitor/network reports we're back online, fire the
  // `online` event graphcache listens for so it flushes its queue.
  const wasOnline = useRef(isOnline)
  useEffect(() => {
    if (isOnline && !wasOnline.current && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('online'))
    }
    wasOnline.current = isOnline
  }, [isOnline])

  const forceSync = useCallback(async () => {
    setIsSyncing(true)
    try {
      // Flush the graphcache offline queue (replay is bound to `online`).
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('online'))
      }
      // Drain the replay BEFORE refetching — otherwise the network-only query
      // can return pre-replay server state and overwrite the optimistic cache.
      // flushQueue empties the persisted metadata at dispatch time, so the
      // serializer's in-flight counter is the signal that replay finished.
      const deadline = Date.now() + 10_000
      let drained = false
      while (!drained && Date.now() < deadline) {
        const metadata = await cache.readMetadata?.()
        const queued = Array.isArray(metadata) ? metadata.length : 0
        drained = queued === 0 && offlineSyncStore.getInFlightMutations() === 0
        if (!drained) {
          await new Promise((resolve) => setTimeout(resolve, 250))
        }
      }
      // Everything landed — past failures are no longer the current state
      if (drained) offlineSyncStore.clearFailures()
      // Photos ride their own queue (ADR-0006) — flush it too, ignoring backoff
      await syncPhotos({ force: true })
      // Refresh the time clock from the network; this also advances lastSyncAt
      // via the sync tracker exchange.
      await client
        .query(GetTimeClockQuery, {}, { requestPolicy: 'network-only' })
        .toPromise()
      await refreshPending()
    } finally {
      setIsSyncing(false)
    }
  }, [client, cache, refreshPending, syncPhotos])

  // Queued photos are pending work too — "Up to date" must not show while
  // job-site photos still sit on disk. `failed` ones are excluded: they will
  // not drain on their own, so they must not hold org switching hostage.
  const activePhotoCount = pendingPhotos.filter(
    (entry) => entry.state !== 'failed',
  ).length
  const failedPhotoCount = pendingPhotos.length - activePhotoCount
  const totalPendingCount = pendingCount + activePhotoCount
  // ...but they are failures the sync screen must still show
  const totalFailureCount = failureCount + failedPhotoCount

  const value = useMemo(
    () => ({
      lastSyncAt,
      pendingCount: totalPendingCount,
      failureCount: totalFailureCount,
      isSyncing,
      forceSync,
    }),
    [lastSyncAt, totalPendingCount, totalFailureCount, isSyncing, forceSync],
  )

  return (
    <SyncStatusContext.Provider value={value}>
      <SyncFailureToaster failureCount={failureCount} />
      {children}
    </SyncStatusContext.Provider>
  )
}

/**
 * Surfaces background sync failures (queued mutations the server rejected on
 * replay — graphcache silently rolls their optimistic state back). The effect
 * is legitimate: it fires an imperative toast into Ionic's controller in
 * response to an external-store change.
 */
const SyncFailureToaster = ({ failureCount }: { failureCount: number }) => {
  const { _ } = useLingui()
  const [presentToast] = useToast()
  const seenCount = useRef(failureCount)

  useEffect(() => {
    if (failureCount <= seenCount.current) return
    seenCount.current = failureCount
    const message = offlineSyncStore.getLastFailureMessage()
    presentToast({
      color: 'danger',
      duration: 6000,
      message: `${_(msg`A change could not be synced and was rolled back`)}${
        message ? `: ${message}` : ''
      }`,
    })
  }, [failureCount, presentToast, _])

  return null
}
